// TopBar Component
// LazySEM Frontend - Application top bar with secondary actions and context

import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigation } from "../../hooks/useNavigation";
import { useTheme } from "../../hooks/useTheme";
import type { User } from "../../contexts/AuthContext";
import { Button } from "../ui/Button/Button";
import { openCommandPalette } from "../../hooks/useCommandPalette";
import { UserMenu } from "./components/UserMenu/UserMenu";
import { OrganizationSwitcher } from "../OrganizationSwitcher";
import DarkModeToggle from "../DarkModeToggle";
import styles from "./TopBar.module.css";

interface TopBarProps {
  user: User;
  onToggleSidebar: () => void;
  isMobile: boolean;
  isCollapsed: boolean;
  isMenuOpen?: boolean;
  className?: string;
}

export const TopBar: React.FC<TopBarProps> = ({
  user,
  onToggleSidebar,
  isMobile,
  isCollapsed,
  isMenuOpen = false,
  className = "",
}) => {
  const { logout } = useAuth();
  const { navigateTo } = useNavigation();
  const { theme } = useTheme();

  const handleUserMenuAction = (action: string) => {
    switch (action) {
      case "profile":
        navigateTo("/profile");
        break;
      case "settings":
        navigateTo("/settings");
        break;
      case "logout":
        logout();
        break;
      default:
        break;
    }
  };

  return (
    <header className={`${styles.topBar} ${className}`}>
      {/* Left Section - Menu Toggle, Logo, and Breadcrumbs */}
      <div className={styles.leftSection}>
        {/* Mobile Menu Toggle */}
        {isMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleSidebar}
            className={styles.menuToggle}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </Button>
        )}

        {/* Desktop Sidebar Toggle */}
        {!isMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleSidebar}
            className={styles.sidebarToggle}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              {isCollapsed ? (
                <path d="M3 12h18M3 6h18M3 18h18" />
              ) : (
                <path d="M11 19l-7-7 7-7M21 19l-7-7 7-7" />
              )}
            </svg>
          </Button>
        )}

        {/* Logo */}
        <Link to="/dashboard" className={styles.logo}>
          <img
            src={
              theme === "dark"
                ? "/LazySEM-Full-Logo-WHITE.png"
                : "/LazySEMFullLogo-01.png"
            }
            alt="LazySEM"
            className={styles.logoImage}
          />
        </Link>
      </div>

      {/* Right Section - Organization Switcher, Search, Theme Toggle, User Menu */}
      <div className={styles.rightSection}>
        {/* Organization Switcher */}
        {!isMobile && <OrganizationSwitcher />}

        {/* Search Box */}
        <button
          type="button"
          onClick={openCommandPalette}
          className={styles.searchButton}
          aria-label="Search"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          {!isMobile && (
            <span className={styles.searchHint} aria-hidden="true">
              Ctrl / ⌘ K
            </span>
          )}
        </button>


        {/* Dark Mode Toggle */}
        {!isMobile && <DarkModeToggle />}

        {/* User Menu */}
        <UserMenu
          user={user}
          onAction={handleUserMenuAction}
          className={styles.userMenu}
        />
      </div>
    </header>
  );
};

export default TopBar;
